import {
  SourceEntityMigrationType,
  type ISourceEntity,
  ISourceEntityTypes,
} from '@principle-theorem/practice-migrations/interfaces';
import { Timezone, TypeGuard, toFloat } from '@principle-theorem/shared';
import { flow, isNumber, isString, isBoolean } from 'lodash';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';
import {
  KEYSET_PLACEHOLDER,
  convertKeysToCamelCaseFn,
  convertNullToUndefinedFn,
  convertValueFn,
} from '../../../source/source-helpers';
import { EXACT_NULL_SENTINEL } from '../../util/helpers';
import { PatientSourceEntity } from './patient';

export const PATIENT_BALANCES_V2_RESOURCE_TYPE = 'patientBalanceV2';

export const PATIENT_BALANCES_V2_SOURCE_ENTITY: ISourceEntity =
  SourceEntity.init({
    metadata: {
      label: 'Patient Balances V2 List',
      description: '',
      idPrefix: PATIENT_BALANCES_V2_RESOURCE_TYPE,
      migrationType: SourceEntityMigrationType.Automatic,
    },
  });

/**
 * Running account balance per patient (DEBTACC3). Used to
 * reconcile the total of migrated invoices against the source
 * balance. `isBadDebt` flags accounts written off.
 */

export interface IExactV2PatientBalanceResult {
  patient_id: string;
  balance: number;
  is_bad_debt: boolean;
}

export interface IExactV2PatientBalance {
  patientId: string;
  balance: number;
  isBadDebt: boolean;
}

export interface IExactV2PatientBalanceFilters {
  patientId: string;
}

function isExactV2PatientBalance(
  data: unknown,
  logError: boolean
): data is IExactV2PatientBalance {
  return TypeGuard.interface<IExactV2PatientBalance>(
    {
      patientId: isString,
      balance: isNumber,
      isBadDebt: isBoolean,
    },
    logError
  )(data);
}

/**
 * Excludes agreement-holder accounts: once their charges are
 * re-attributed to real patients and their receipts replaced by
 * synthetic per-charge receipts, the holder's balance is meaningless.
 *
 * Aggregated per debtor because DEBTACC3 can hold multiple rows for the same
 * account. The source record id is the debtor id, so ungrouped rows collide
 * and silently discard the balance of all but one of them.
 */
const PATIENT_BALANCES_V2_SOURCE_QUERY = `
WITH agreement_holders AS (
  SELECT DISTINCT payorid AS debtorid
  FROM trans4
  WHERE
    NOT COALESCE(_is_deleted, false)
    AND payorid != ${EXACT_NULL_SENTINEL}
    AND payorid NOT IN (SELECT _record_id FROM localpayor)
  UNION
  SELECT _record_id AS debtorid FROM localpayor
),
account_balances AS (
  SELECT
    debtacc3.debtorid                                 AS debtorid,
    SUM(convert_to_decimal(debtacc3.balance))         AS balance,
    BOOL_OR(convert_to_boolean(debtacc3.isbaddebt))   AS is_bad_debt
  FROM
    debtacc3
  WHERE
    NOT COALESCE(debtacc3._is_deleted, FALSE)
    AND debtacc3.debtorid != ${EXACT_NULL_SENTINEL}
    AND debtacc3.debtorid NOT IN (SELECT debtorid FROM agreement_holders)
  GROUP BY
    debtacc3.debtorid
)
SELECT
  account_balances.debtorid::TEXT  AS patient_id,
  account_balances.balance         AS balance,
  account_balances.is_bad_debt     AS is_bad_debt
FROM
  account_balances
WHERE
  TRUE
${KEYSET_PLACEHOLDER}
`;

export type ExactV2PatientBalance = ISourceEntityTypes<
  IExactV2PatientBalance,
  object,
  IExactV2PatientBalanceFilters
>;

export class PatientBalancesV2SourceEntity extends BaseSourceEntity<ExactV2PatientBalance> {
  sourceEntity = PATIENT_BALANCES_V2_SOURCE_ENTITY;
  entityResourceType = PATIENT_BALANCES_V2_RESOURCE_TYPE;
  sourceQuery = PATIENT_BALANCES_V2_SOURCE_QUERY;
  verifySourceFn = isExactV2PatientBalance;
  override defaultOffsetSize = 50000;
  override keysetPagination = {
    cursorColumn: 'account_balances.debtorid',
    cursorField: 'patientId',
  };

  override requiredEntities = {
    patients: new PatientSourceEntity(),
  };

  override transformDataFn = flow([
    convertNullToUndefinedFn<IExactV2PatientBalanceResult>(),
    convertValueFn(toFloat, 'balance'),
    convertKeysToCamelCaseFn<IExactV2PatientBalanceResult>(),
  ]);

  getSourceRecordId(data: IExactV2PatientBalance): string {
    return data.patientId;
  }

  getSourceLabel(record: IExactV2PatientBalance): string {
    return record.patientId;
  }

  translate(_data: IExactV2PatientBalance, _timezone: Timezone): object {
    return {};
  }

  getFilterData(
    data: IExactV2PatientBalance,
    _timezone: Timezone
  ): IExactV2PatientBalanceFilters {
    return {
      patientId: data.patientId,
    };
  }
}
